/* ============================================================================
 * ГЕОДЕЗИЧЕСКИЕ РАСЧЁТЫ — WGS-84 (сфера, формула гаверсинуса)
 * Все высоты в метрах, скорости в м/с, азимуты в градусах истинного севера.
 * ========================================================================= */

/** Средний радиус Земли WGS-84, м */
export const EARTH_RADIUS_M = 6371008.8

export interface LatLon {
  lat: number
  lon: number
}

export interface BoundingBox {
  lat1: number
  lon1: number
  lat2: number
  lon2: number
}

const rad = (deg: number): number => (deg * Math.PI) / 180
const deg = (r: number): number => (r * 180) / Math.PI

/** Проверка корректности координаты */
export function isValidCoord(lat: number, lon: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  )
}

/**
 * Расстояние между двумя точками по большому кругу, метры.
 * Используется в инструменте «Дальность и азимут» (Range & Bearing).
 */
export function distanceM(a: LatLon, b: LatLon): number {
  const dLat = rad(b.lat - a.lat)
  const dLon = rad(b.lon - a.lon)
  const la1 = rad(a.lat)
  const la2 = rad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Истинный азимут от точки A к точке B, градусы 0..360 (0° — север, по часовой).
 */
export function bearingDeg(a: LatLon, b: LatLon): number {
  const la1 = rad(a.lat)
  const la2 = rad(b.lat)
  const dLon = rad(b.lon - a.lon)
  const y = Math.sin(dLon) * Math.cos(la2)
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon)
  return (deg(Math.atan2(y, x)) + 360) % 360
}

/** Радиансное смещение от точки на dLat/dLon — для отрисовки розы направлений */
export function offsetM(origin: LatLon, eastM: number, northM: number): LatLon {
  return {
    lat: origin.lat + deg(northM / EARTH_RADIUS_M),
    lon: origin.lon + deg(eastM / (EARTH_RADIUS_M * Math.cos(rad(origin.lat)) || 1)),
  }
}

/** Границы полигона; null, если координат меньше трёх */
export function bboxOfPolygon(poly: Array<[number, number]>): BoundingBox | null {
  const pts = poly.filter(([lon, lat]) => isValidCoord(lat, lon))
  if (pts.length < 3) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  for (const [lon, lat] of pts) {
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lon < minLon) minLon = lon
    if (lon > maxLon) maxLon = lon
  }
  return { lat1: minLat, lon1: minLon, lat2: maxLat, lon2: maxLon }
}

/** Расширяет прямоугольник на долю от его размера — чтобы запрос /api/traffic
 *  не упирался в границы видимой области */
export function padBbox(box: BoundingBox, factor = 0.15): BoundingBox {
  const dLat = (box.lat2 - box.lat1) * factor
  const dLon = (box.lon2 - box.lon1) * factor
  return {
    lat1: Math.max(-90, box.lat1 - dLat),
    lon1: Math.max(-180, box.lon1 - dLon),
    lat2: Math.min(90, box.lat2 + dLat),
    lon2: Math.min(180, box.lon2 + dLon),
  }
}

/** Проверка попадания точки в прямоугольник */
export function inBbox(box: BoundingBox, p: LatLon): boolean {
  return p.lat >= box.lat1 && p.lat <= box.lat2 && p.lon >= box.lon1 && p.lon <= box.lon2
}

/**
 * Ширина маршрута БВС, метры — максимальный поперечный размах маршрута.
 * Требование ТЗ: в классах G и H ширина маршрута должна быть ≤ 4 км.
 *
 * Оценка берётся по минимальному поворотному углу между сегментами: при резком
 * развороте (≈90°) поперечный размах равен длине пути от вершины до конца маршрута.
 */
export function routeWidthM(route: Array<[number, number]>): number {
  const pts = route.filter(([lon, lat]) => isValidCoord(lat, lon))
  if (pts.length < 2) return 0

  const toLatLon = ([lon, lat]: [number, number]): LatLon => ({ lat, lon })
  const P = pts.map(toLatLon)

  // Проекция в локальную плоскость (метры) — на масштабе района погрешность ничтожна
  const lat0 = rad(P[0].lat)
  const mx = (p: LatLon): number => (rad(p.lon) - rad(P[0].lon)) * EARTH_RADIUS_M * Math.cos(lat0)
  const my = (p: LatLon): number => (rad(p.lat) - lat0) * EARTH_RADIUS_M

  const Q = P.map((p) => ({ x: mx(p), y: my(p) }))
  let width = 0

  for (let i = 1; i < Q.length - 1; i++) {
    const ax = Q[i].x - Q[i - 1].x
    const ay = Q[i].y - Q[i - 1].y
    const bx = Q[i + 1].x - Q[i].x
    const by = Q[i + 1].y - Q[i].y
    const lenA = Math.hypot(ax, ay)
    const lenB = Math.hypot(bx, by)
    if (lenA < 1e-6 || lenB < 1e-6) continue

    // Косинус угла между векторами подхода и ухода
    const cos = Math.min(1, Math.max(-1, (ax * bx + ay * by) / (lenA * lenB)))
    const turn = Math.acos(cos) // 0 — прямая, π — разворот назад
    if (turn < 0.15) continue // почти прямой участок — разворот не формирует ширину

    // Поперечный размах: проекция длины хвоста на нормаль к входящему сегменту
    const tailX = Q[Q.length - 1].x - Q[i].x
    const tailY = Q[Q.length - 1].y - Q[i].y
    const cross = Math.abs((-ay / lenA) * tailX + (ax / lenA) * tailY)
    if (cross > width) width = cross
  }

  // Для ломаной без поворотов (прямой маршрут) ширина равна длине самого
  // короткого прямого отрезка — консервативная оценка сверху
  if (width === 0 && Q.length >= 2) {
    const last = Q[Q.length - 1]
    width = Math.hypot(last.x - Q[0].x, last.y - Q[0].y)
  }

  return width
}

/**
 * Форматирование координат по ГОСТ Р 71934-2025 (десятичные градусы, WGS-84).
 * Вид: 55°45'12.34" с.д., 37°37'01.23" в.д.
 */
export function formatLatLon(lat: number, lon: number, withHemisphere = true): string {
  if (!isValidCoord(lat, lon)) return '—'
  const fmt = (v: number, pos: string, neg: string): string => {
    const hemi = withHemisphere ? (v >= 0 ? pos : neg) : ''
    const abs = Math.abs(v)
    const d = Math.floor(abs)
    const mFloat = (abs - d) * 60
    const m = Math.floor(mFloat)
    const s = (mFloat - m) * 60
    return `${d}°${String(m).padStart(2, '0')}'${s.toFixed(2).padStart(5, '0')}"${hemi}`
  }
  return `${fmt(lat, 'с.ш.', 'ю.ш.')} ${fmt(lon, 'в.д.', 'з.д.')}`
}

/** Компактный десятичный вид — для полей ввода и таблиц */
export function formatDecimal(lat: number, lon: number, digits = 5): string {
  if (!isValidCoord(lat, lon)) return '—'
  return `${lat.toFixed(digits)}, ${lon.toFixed(digits)}`
}
